import Axios from 'axios';

// 飞书项目开放接口，走 /feishuReport 代理（vite dev / nginx prod）转发到 project.feishu.cn
const FEISHU_BASE = '/feishuReport';
// 问题提报所在飞书项目 project_key
export const FEISHU_PROJECT_KEY = '677f2f2199b83aa2fc92e07f';

const PLUGIN_ID = 'MII_67A6030B60C38013';
const PLUGIN_SECRET = '4030FEBE66742B286F67DDA45CB24176';

// 独立实例，避免走项目默认 axios 的 sso 拦截器
const feishu = Axios.create({
  baseURL: FEISHU_BASE,
  timeout: 15000,
  headers: {
    'content-type': 'application/json',
    'X-USER-KEY': '7362226979170418690',
  },
});

// 网关/上游偶发超时（504）或网络抖动时可重试的错误
const isRetriableError = (error: any) => {
  const status = error?.response?.status;
  if (status) return status === 502 || status === 503 || status === 504;
  // 无响应：超时或网络中断（ECONNABORTED / Network Error）
  return true;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * 带重试的飞书 POST。field/all 等接口上游偶发 504，重试后即可成功。
 * @param retries 额外重试次数（首次失败后最多再试 retries 次）
 */
const postWithRetry = async (
  url: string,
  data: any,
  config: any,
  retries = 3,
) => {
  let lastError: any;
  for (let attempt = 0; attempt <= retries; attempt += 1) {
    try {
      return await feishu.post(url, data, config);
    } catch (error) {
      lastError = error;
      if (attempt === retries || !isRetriableError(error)) break;
      // 递增退避：300ms / 600ms / 900ms
      await delay(300 * (attempt + 1));
    }
  }
  throw lastError;
};

const TOKEN_KEY = 'fsTokenInfo';

const storeTokenInfo = (tokenInfo: any) => {
  const expire_time = Date.now() + tokenInfo.expire_time * 1000;
  localStorage.setItem(TOKEN_KEY, JSON.stringify({ ...tokenInfo, expire_time }));
};

const getStoredToken = () => localStorage.getItem(TOKEN_KEY);

const isTokenExpired = () => {
  const stored = getStoredToken();
  if (!stored) return true;
  return Date.now() >= JSON.parse(stored).expire_time;
};

const refreshFsToken = async () => {
  try {
    const res = await feishu.post('/open_api/authen/plugin_token', {
      plugin_id: PLUGIN_ID,
      plugin_secret: PLUGIN_SECRET,
    });
    if (res.data?.data) {
      storeTokenInfo(res.data.data);
    }
  } catch (error) {
    console.error('刷新飞书 token 失败', error);
  }
};

// 保证 token 可用，返回请求头
const withPluginToken = async (extra: Record<string, string> = {}) => {
  if (isTokenExpired()) {
    await refreshFsToken();
  }
  const stored = getStoredToken();
  const headers: Record<string, string> = { ...extra };
  if (stored) {
    headers['X-PLUGIN-TOKEN'] = JSON.parse(stored).token;
  }
  return headers;
};

/**
 * 研发人员搜索：输入关键字请求飞书成员
 * @returns 飞书响应体，data 为成员数组（含 name_cn / user_key）
 */
export const getFeishuMembers = async (params: { query: string }) => {
  const headers = await withPluginToken();
  const res = await postWithRetry('/open_api/user/search', params, { headers });
  return res.data;
};

/**
 * 获取项目全部字段定义（负责组等下拉选项由此透传）
 * @returns 飞书响应体，data 为字段定义数组（含 field_key / options / field_type_key）
 */
export const getField = async () => {
  const headers = await withPluginToken();
  const res = await postWithRetry(
    `/open_api/${FEISHU_PROJECT_KEY}/field/all`,
    {},
    { headers },
  );
  return res.data;
};

